/*
PURPOSE: Fetch and shape recent public coral contributions for visualisation.
USE CASES:
- RecentCoralVisualisation (legacy CoralChart-based recent corals strip)
- Home2 RecentCoralsBloom wrapper (CoralBloom-based recent corals)
DOMAIN BOUNDARIES:
- Read-only fetch of the public S3 `recent-contributions.json` for the active
  stage. No writes, no auth, no analytics.
- Extracted verbatim from RecentCoralVisualisation so both the legacy strip and
  the editorial bloom render from a single source of truth (no duplicated
  fetch/processing that could silently drift).
ASSUMPTIONS:
- Stage comes from REACT_APP_ENVIRONMENT (defaults to 'dev'), matching the
  prior inline behaviour exactly.
*/

import { useState, useEffect, useCallback } from 'react';

export interface Contribution {
  artistName: string;
  amount: number;
  topGenre: string;
  topCategory: string;
  transactionId: string;
}

export interface CoralData {
  transactionId: string;
  totalAmount: number;
  genres: { name: string; amount: number }[];
  artists: { name: string; amount: number }[];
}

// Exported for unit testing — shared aggregation feeding both the legacy
// recent-corals strip and the Home2 RecentCoralsBloom.
export const processContributions = (contributions: Contribution[]): CoralData[] => {
  const coralMap = new Map<string, CoralData>();

  contributions.forEach((contribution) => {
    if (!coralMap.has(contribution.transactionId)) {
      coralMap.set(contribution.transactionId, {
        transactionId: contribution.transactionId,
        totalAmount: 0,
        genres: [],
        artists: [],
      });
    }

    const coral = coralMap.get(contribution.transactionId)!;
    coral.totalAmount += contribution.amount;

    const genreIndex = coral.genres.findIndex((g) => g.name === contribution.topCategory);
    if (genreIndex === -1) {
      coral.genres.push({ name: contribution.topCategory, amount: contribution.amount });
    } else {
      coral.genres[genreIndex].amount += contribution.amount;
    }

    const artistIndex = coral.artists.findIndex((a) => a.name === contribution.artistName);
    if (artistIndex === -1) {
      coral.artists.push({ name: contribution.artistName, amount: contribution.amount });
    } else {
      coral.artists[artistIndex].amount += contribution.amount;
    }
  });

  return Array.from(coralMap.values());
};

interface UseRecentContributionsResult {
  coralData: CoralData[];
  isLoading: boolean;
  error: string | null;
}

export const useRecentContributions = (): UseRecentContributionsResult => {
  const [coralData, setCoralData] = useState<CoralData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContributions = useCallback(async () => {
    // Bound the request so a stalled S3/CDN response degrades to the error
    // state instead of an indefinite loading spinner (now on the homepage).
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const stage = process.env.REACT_APP_ENVIRONMENT || 'dev';
      const url = `https://wearecoral-public-data-${stage}.s3.ap-southeast-2.amazonaws.com/recent-contributions.json`;
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        throw new Error('Failed to fetch contributions');
      }
      const data: Contribution[] = await response.json();
      const processedData = processContributions(data);
      setCoralData(processedData);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching contributions:', err);
      setError('Failed to load recent contributions');
      setIsLoading(false);
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    fetchContributions();
  }, [fetchContributions]);

  return { coralData, isLoading, error };
};
